<template>
  <div class="view" v-if="canView" v-cloak>
    <GridPanelHeader
      :title="$t('scale.quantity_warnings.title')"
      :grid-ref="$refs[gridRefKey]"
      :show-add="canEdit"
      :show-filter="false"
      :show-export="true"
      :show-columns="true"
      @add="handleAddClick"
      @export="exportGrid"
      @columns="openColumnChooser"
    />

    <div class="color-labels" style="margin-bottom: 16px; display: flex; gap: 16px; align-items: center; justify-content: flex-end">
      <span style="display: flex; align-items: center"> {{ $t("common.remaining_percentage") }}: </span>
      <span style="display: flex; align-items: center">
        <span
          style="
            display: inline-block;
            width: 12px;
            height: 12px;
            background: #ea652a;
            border-radius: 3px;
            margin-right: 6px;
            border: 1px solid #ccc;
          "
        ></span>
        <span style="color: #ea652a; font-weight: 500"> <= 0%</span>
      </span>
      <span style="display: flex; align-items: center">
        <span
          style="
            display: inline-block;
            width: 12px;
            height: 12px;
            background: #dc3545;
            border-radius: 3px;
            margin-right: 6px;
            border: 1px solid #ccc;
          "
        ></span>
        <span style="color: #dc3545; font-weight: 500"> > 0%</span>
      </span>
    </div>

    <DxDataGrid
      :ref="gridRefKey"
      :data-source="quantityWarningsDataSource"
      :remote-operations="gridRemoteOperations"
      :column-min-width="80"
      style="min-height: 350px"
      :show-column-lines="true"
      :show-row-lines="false"
      :show-borders="true"
      :column-auto-width="true"
      :allowColumnReordering="true"
      :allowColumnResizing="true"
      :row-alternation-enabled="true"
      :element-attr="gridAttributes"
      @editing-start="onEditingStart"
      @option-changed="onOptionChanged"
      @content-ready="onContentReady"
      @row-prepared="onRowPrepared"
      :row-height="50"
    >
      <DxColumn
        dataField="legal_entity_name"
        :caption="$t('common.legal_entity_name')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
      />
      <DxColumn
        dataField="year"
        :caption="$t('year')"
        data-type="number"
        :allowHeaderFiltering="false"
        :filter-operations="['=']"
        :width="90"
      />
      <DxColumn
        dataField="updated_at"
        :caption="$t('common.updated_at')"
        data-type="date"
        :allowHeaderFiltering="false"
        :allowFiltering="true"
        :allowSorting="true"
        :filter-operations="['contains']"
        :format="getDxGridDefaultDateFormat + ' HH:mm'"
        :editorOptions="{ displayFormat: getDxGridDefaultDateFormat + ' HH:mm', disabled: true }"
        sort-order="desc"
        :sort-index="0"
      />
      <DxColumn dataField="product_name" :caption="$t('common.product')" :allowHeaderFiltering="false" :filter-operations="['contains']" />
      <DxColumn
        dataField="quantity_limit"
        :caption="$t('scale.quantity_warnings.quantity_limit')"
        data-type="number"
        :allowHeaderFiltering="false"
        :allowFiltering="true"
        value-format="#,##0.##"
      />
      <DxColumn
        dataField="fulfilled_quantity"
        :caption="$t('scale.quantity_warnings.fulfilled_quantity')"
        data-type="number"
        :allowHeaderFiltering="false"
        :allowFiltering="false"
        value-format="#,##0.##"
      />
      <DxColumn
        dataField="remaining_quantity"
        :caption="$t('scale.quantity_warnings.remaining_quantity')"
        data-type="number"
        :allowHeaderFiltering="false"
        :allowFiltering="false"
        value-format="#,##0.##"
      />
      <DxColumn
        dataField="remaining_percentage"
        :caption="$t('scale.quantity_warnings.remaining_percentage')"
        data-type="number"
        :allowHeaderFiltering="false"
        :allowFiltering="false"
        :width="120"
        cell-template="percentageCell"
      />
      <template #percentageCell="{ data }">
        <span>{{ data.value !== null && data.value !== undefined ? data.value.toFixed(1) + " %" : "" }}</span>
      </template>
      <DxColumn
        dataField="measure_unit"
        :caption="$t('common.measure_unit')"
        :allowHeaderFiltering="false"
        :allowFiltering="true"
        :width="100"
      />
      <DxColumn
        :width="70"
        alignment="center"
        :fixed="true"
        fixed-position="right"
        :allow-sorting="false"
        :allow-filtering="false"
        :show-in-column-chooser="false"
        cell-template="actionsCell"
      />
      <template #actionsCell="{ data }">
        <DxActionMenu
          :grid-ref="$refs[gridRefKey]"
          row-key="uuid"
          :row="data.data"
          :can-edit="canEdit"
          :can-delete="canDelete"
          :allow-deleting="allowDeletingRow"
        />
      </template>

      <DxEditing mode="row" :allowUpdating="false" :allowAdding="false" :allowDeleting="false" :confirm-delete="true" :use-icons="true">
        <DxTexts
          :confirm-delete-message="$t('common.confirm_delete', { title: $t('scale.quantity_warnings.title_edit') })"
          :confirm-delete-title="$t('common.confirm_delete_title')"
          :deleteRow="$t('common.delete')"
        />
      </DxEditing>

      <DxPaging :page-size="15" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="pageSizes"
        display-mode="compact"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
        :info-text="$t('common.page_info')"
      />
      <DxHeaderFilter :visible="true" />
      <DxScrolling show-scrollbar="always" :use-native="false" />
      <DxFilterRow :visible="true" :showOperationChooser="true" apply-filter="auto" />
      <DxSelection mode="single" />
      <DxSearchPanel :visible="false" :width="240" />
      <DxGroupPanel :visible="false" :empty-panel-text="$t('common.group_panel_placeholder')" />
      <DxExport :enabled="false" :allow-export-selected-data="false" :fileName="'QuantityWarnings'" />
      <DxColumnChooser :enabled="false" mode="select" />
      <DxColumnFixing :enabled="true" />

      <DxMasterDetail :enabled="true" template="detailTemplate" />
      <template #detailTemplate="{ data }">
        <QuantityWarningDetailList :row-data="data.data" />
      </template>
    </DxDataGrid>
  </div>
  <div v-else>
    <Forbidden />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DxDataGrid, {
  DxColumn,
  DxPaging,
  DxPager,
  DxEditing,
  DxHeaderFilter,
  DxFilterRow,
  DxSelection,
  DxTexts,
  DxSearchPanel,
  DxExport,
  DxGroupPanel,
  DxColumnChooser,
  DxColumnFixing,
  DxScrolling,
  DxMasterDetail,
} from "devextreme-vue/data-grid";
import Forbidden from "~/pages/errors/Forbidden.vue";
import CustomStore from "devextreme/data/custom_store";
import { canView, canDelete, canEdit, permissions, permissionTypes } from "~/helpers/Permissions";
import { format } from "date-fns";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import { saveDxGridColumnSettings, loadDxGridColumnSettings } from "@/helpers/dxGridHelper";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import DxActionMenu from "@/components/DxGrid/DxActionMenu.vue";
import QuantityWarningDetailList from "./partials/QuantityWarningDetailList.vue";

export default {
  name: "QuantityWarningsList",
  components: {
    DxDataGrid,
    DxColumn,
    DxEditing,
    DxHeaderFilter,
    DxFilterRow,
    DxSelection,
    DxTexts,
    DxPaging,
    DxPager,
    Forbidden,
    DxSearchPanel,
    DxExport,
    DxGroupPanel,
    DxColumnChooser,
    DxColumnFixing,
    DxScrolling,
    DxMasterDetail,
    GridPanelHeader,
    DxActionMenu,
    QuantityWarningDetailList,
  },
  data() {
    return {
      gridAttributes: {
        class: "smagria-grid quantity-warnings-grid",
      },
      gridRefKey: "quantityWarningsGrid",
      quantityWarningsDataSource: new CustomStore({
        key: "uuid",
        load: async (loadOptions) => {
          let dataSource = {
            data: [],
            totalCount: 0,
          };

          if (this.visibilitySettingsLoading || !this.visibilitySettingsLoaded) {
            return dataSource;
          }

          let pageLimit = loadOptions.isLoadingAll ? 99999 : loadOptions.take;
          let pageNumber = (loadOptions.skip + loadOptions.take) / pageLimit;

          let payload = {
            page: pageNumber,
            limit: pageLimit,
            filter: loadOptions.filter,
            sort: loadOptions.sort,
          };

          try {
            await this.getQuantityWarnings(payload).then((response) => {
              dataSource.data = response.data?.data || response.data || [];
              dataSource.totalCount = response.data?.pagination?.total ?? 1;
            });
            return dataSource;
          } catch (error) {
            console.error("Error fetching quantity warnings:", error);
            throw error;
          }
        },
        remove: (key) => this.removeQuantityWarning(key),
      }),
      gridRemoteOperations: {
        paging: true,
        filtering: true,
        sorting: true,
      },
      pageSizes: [15, 50, 100],
      visibilitySettingsLoaded: false,
      visibilitySettingsLoading: false,
      saveColumnSettingsTimeout: null,
    };
  },
  computed: {
    ...mapGetters({
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
    }),
    config() {
      return {
        permission: permissions.QUANTITY_WARNINGS,
        permission_type: permissionTypes.access,
      };
    },
    canView() {
      return canView(this);
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
    dataGridInstance() {
      const grid = this.$refs[this.gridRefKey];
      return grid ? grid.instance : null;
    },
  },
  methods: {
    ...mapActions({
      getQuantityWarnings: "scaleQuantityWarnings/getQuantityWarnings",
      deleteQuantityWarning: "scaleQuantityWarnings/deleteQuantityWarning",
      getDxGridSettings: "dxgrid/getDxGridSettings",
      setDxGridSettings: "dxgrid/setDxGridSettings",
    }),
    handleAddClick() {
      this.$router.push({
        name: "quantity-warnings-edit",
        params: { uuid: "add" },
      });
    },
    allowDeletingRow(row) {
      return this.canDelete && !!row;
    },
    onEditingStart(e) {
      e.cancel = true;
      this.$router.push({
        name: "quantity-warnings-edit",
        params: { uuid: e.data.quantity_warning_uuid },
      });
    },
    onRowPrepared(e) {
      if (e.rowType === "data") {
        if (e.data.fulfilled_quantity < e.data.quantity_limit) {
          e.rowElement.classList.add("smagria-danger-row");
        } else {
          e.rowElement.classList.add("smagria-warning-row");
        }
      }
    },
    async removeQuantityWarning(key) {
      const row = this.dataGridInstance
        ?.getDataSource()
        ?.items()
        ?.find((r) => r.uuid === key);
      const uuid = row?.uuid;
      if (!uuid) return;
      try {
        await this.deleteQuantityWarning({ uuid });
      } catch (error) {
        console.error("Error deleting quantity warning:", error);
        throw error;
      }
    },
    openColumnChooser() {
      if (this.dataGridInstance) {
        this.dataGridInstance.showColumnChooser();
      }
    },
    exportGrid() {
      const instance = this.dataGridInstance;
      if (!instance) return;
      const fileName = "QuantityWarnings_" + format(new Date(), "yyyy_MM_dd_HH_mm_ss");
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("QuantityWarnings");
      exportDataGrid({ component: instance, worksheet, autoFilterEnabled: true }).then(() => {
        workbook.xlsx.writeBuffer().then((buffer) => {
          saveAs(new Blob([buffer], { type: "application/octet-stream" }), fileName + ".xlsx");
        });
      });
    },
    onOptionChanged(e) {
      if (e && e.fullName === "paging.pageSize" && e.value !== e.previousValue) {
        if (this.dataGridInstance) {
          this.dataGridInstance.option("paging.pageIndex", 0);
        }
        this.dataGridInstance?.refresh();
      } else {
        saveDxGridColumnSettings(this, e);
      }
    },
    onContentReady() {
      loadDxGridColumnSettings(this);
    },
  },
  mounted() {},
};
</script>

<style>
.quantity-warnings-grid {
  height: calc(95vh - 200px);
}
</style>
